/**
 * Zero-Knowledge Therapy Notes Encryption
 *
 * Each note is encrypted with AES-256-GCM using the therapist's master key.
 * The platform never has access to the unencrypted notes or the master key.
 */

const IV_LENGTH = 12;

/**
 * Encrypted note structure
 */
export interface EncryptedNote {
  /** Base64-encoded ciphertext */
  ciphertext: string;
  /** Base64-encoded initialization vector */
  iv: string;
  /** Encryption version for future compatibility */
  version: number;
}

/**
 * Import raw master key bytes as a CryptoKey for encryption/decryption
 */
async function importMasterKey(masterKeyBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    masterKeyBytes.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a therapy note
 *
 * @param plaintext - The note content (string)
 * @param masterKey - The therapist's master key (32 bytes)
 * @returns Encrypted note object ready for storage
 */
export async function encryptNote(
  plaintext: string,
  masterKey: Uint8Array
): Promise<EncryptedNote> {
  const encoder = new TextEncoder();
  const plaintextBytes = encoder.encode(plaintext);

  // Generate unique IV for each note
  const iv = new Uint8Array(IV_LENGTH);
  crypto.getRandomValues(iv);

  // Import master key
  const cryptoKey = await importMasterKey(masterKey);

  // Encrypt
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    cryptoKey,
    plaintextBytes.buffer as ArrayBuffer
  );

  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertextBuffer)),
    iv: bytesToBase64(iv),
    version: 1,
  };
}

/**
 * Decrypt a therapy note
 *
 * @param encryptedNote - The encrypted note object
 * @param masterKey - The therapist's master key (32 bytes)
 * @returns Decrypted note content
 */
export async function decryptNote(
  encryptedNote: EncryptedNote,
  masterKey: Uint8Array
): Promise<string> {
  const ciphertext = base64ToBytes(encryptedNote.ciphertext);
  const iv = base64ToBytes(encryptedNote.iv);

  // Import master key
  const cryptoKey = await importMasterKey(masterKey);

  // Decrypt
  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    cryptoKey,
    ciphertext.buffer as ArrayBuffer
  );

  const decoder = new TextDecoder();
  return decoder.decode(plaintextBuffer);
}

/**
 * Encrypt a therapy note and return as a single base64 blob
 * Format: version (1 byte) + iv (12 bytes) + ciphertext
 */
export async function encryptNoteToBlob(
  plaintext: string,
  masterKey: Uint8Array
): Promise<string> {
  const encrypted = await encryptNote(plaintext, masterKey);
  const iv = base64ToBytes(encrypted.iv);
  const ciphertext = base64ToBytes(encrypted.ciphertext);

  // Combine: version + iv + ciphertext
  const blob = new Uint8Array(1 + iv.length + ciphertext.length);
  blob[0] = encrypted.version;
  blob.set(iv, 1);
  blob.set(ciphertext, 1 + iv.length);

  return bytesToBase64(blob);
}

/**
 * Decrypt a therapy note from a single base64 blob
 */
export async function decryptNoteFromBlob(
  blob: string,
  masterKey: Uint8Array
): Promise<string> {
  const bytes = base64ToBytes(blob);

  const version = bytes[0];
  if (version !== 1) {
    throw new Error(`Unsupported encryption version: ${version}`);
  }

  const iv = bytes.slice(1, 1 + IV_LENGTH);
  const ciphertext = bytes.slice(1 + IV_LENGTH);

  const encryptedNote: EncryptedNote = {
    ciphertext: bytesToBase64(ciphertext),
    iv: bytesToBase64(iv),
    version,
  };

  return decryptNote(encryptedNote, masterKey);
}

// Helper functions
function bytesToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
